import { TaskCompletionChart } from '@/components/dashboard/charts/task-completion-chart';
import { PerformanceMetrics } from '@/components/dashboard/charts/performance-metrics';
import { TaskLocationMap } from '@/components/dashboard/maps/task-location-map';
import { TaskStatusCard } from '@/components/dashboard/cards/task-status-card';
import { FeedbackSummaryCard } from '@/components/dashboard/cards/feedback-summary-card';
import { ActiveTaskCard } from '@/components/dashboard/cards/active-task-card';
import { UpcomingTasksCard } from '@/components/dashboard/cards/upcoming-tasks-card';
import { Clock, MapPin, Star, CheckCircle2, Wrench, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';

export function HelperDashboard() {
  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-primary/10 p-2 flex items-center justify-center">
            <Wrench className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300">
              FlexHelper Dashboard
            </h1>
            <p className="text-muted-foreground">
              Monitor your tasks and performance
            </p>
          </div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground flex items-center gap-2"
        >
          <Calendar className="h-4 w-4 text-primary" />
          <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
        </motion.div>
      </motion.div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <TaskStatusCard
          title="Today's Tasks"
          value="5"
          description="2 completed"
          icon={Clock}
          trend={{
            data: [
              { name: '9AM', value: 1 },
              { name: '11AM', value: 2 },
              { name: '1PM', value: 3 },
              { name: '3PM', value: 4 },
              { name: '5PM', value: 5 },
            ],
            description: "Task progress throughout today"
          }}
          details={{
            description: "Current task status",
            items: [
              { label: "Completed", value: "2" },
              { label: "In Progress", value: "1" },
              { label: "Upcoming", value: "2" },
              { label: "Average Time", value: "45m" }
            ]
          }}
        />

        <TaskStatusCard
          title="Current Location"
          value="Active"
          description="GPS tracking on"
          icon={MapPin}
          details={{
            description: "Location details",
            items: [
              { label: "Current Zone", value: "Downtown" },
              { label: "Next Task", value: "2.5 miles" },
              { label: "Travel Time", value: "15 min" },
              { label: "Tasks Nearby", value: "3" }
            ]
          }}
        />

        <TaskStatusCard
          title="Performance Rating"
          value="4.8"
          description="Last 30 days"
          icon={Star}
          trend={{
            data: [
              { name: 'Week 1', value: 4.6 },
              { name: 'Week 2', value: 4.7 },
              { name: 'Week 3', value: 4.8 },
              { name: 'Week 4', value: 4.8 },
            ],
            description: "Rating trend over time"
          }}
          details={{
            description: "Rating breakdown",
            items: [
              { label: "5★ Reviews", value: "85%" },
              { label: "4★ Reviews", value: "12%" },
              { label: "Response Time", value: "15m" },
              { label: "Completion Rate", value: "98%" }
            ]
          }}
        />

        <TaskStatusCard
          title="Task Completion"
          value="98%"
          description="On-time completion rate"
          icon={CheckCircle2}
          trend={{
            data: [
              { name: 'Mon', value: 95 },
              { name: 'Tue', value: 98 },
              { name: 'Wed', value: 97 },
              { name: 'Thu', value: 99 },
              { name: 'Fri', value: 98 },
            ],
            description: "Completion rate trend"
          }}
          details={{
            description: "Task statistics",
            items: [
              { label: "Total Tasks", value: "156" },
              { label: "On Time", value: "153" },
              { label: "Delayed", value: "3" },
              { label: "Avg Duration", value: "42m" }
            ]
          }}
        />
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <ActiveTaskCard />
        </Card>
        <Card variant="glass" className="overflow-hidden p-1">
          <TaskLocationMap />
        </Card>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <TaskCompletionChart />
        </Card>
        <Card variant="glass" className="overflow-hidden p-1">
          <PerformanceMetrics />
        </Card>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <UpcomingTasksCard />
        </Card>
        <Card variant="glass" className="overflow-hidden p-1">
          <FeedbackSummaryCard />
        </Card>
      </motion.div>
    </div>
  );
}